/** @jsx jsx */

import { useEffect, useState } from 'react';

import { Colors, Base } from '@zapier/theme';
import { css, jsx } from '@emotion/core';

type ProgressBarProps = {
  /** Determines whether to initially set percent to 0 so that the bar is animated */
  animateInitial?: boolean;
  /** The border radius of the bar */
  borderRadius?: string | number;
  /** Color of the value bar. */
  color?: keyof typeof Base;
  /** The height of the bar */
  height?: string | number;
  /** Percent of the value (0-100). */
  percent?: number;
};

type ProgressBarCssProps = {
  borderRadius: string | number;
  height: string | number;
};

type ProgressBarInitialValueProps = {
  animateInitial: boolean;
  percent: number;
};

const getProgressBarCss = ({ borderRadius, height }: ProgressBarCssProps) =>
  css({
    height,
    backgroundColor: Colors.adamantium,
    borderRadius,
    overflow: 'hidden',
    position: 'relative',
  });

const amountBaseCss = css({
  position: 'absolute',
  top: 0,
  left: 0,
  width: 0,
  height: 'inherit',
  borderRadius: 'inherit',
  transition: 'all 0.6s ease-in-out',
});

const getInitialValue = ({
  animateInitial,
  percent,
}: ProgressBarInitialValueProps) => {
  return animateInitial ? 0 : percent;
};

/**
 * Progress Bar.
 */
const ProgressBar = ({
  animateInitial = false,
  borderRadius = 100,
  color = 'joker',
  height = 9,
  percent = 0,
}: ProgressBarProps) => {
  const [actual, setActual] = useState<number>(
    getInitialValue({ animateInitial, percent })
  );

  useEffect(() => {
    if (animateInitial) {
      setActual(percent);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const amountCss = css(amountBaseCss, {
    backgroundColor: Colors[color],
  });

  const formattedPercent = `${actual}%`;

  return (
    <div css={getProgressBarCss({ borderRadius, height })}>
      <div
        css={amountCss}
        style={{ width: formattedPercent }}
        title={formattedPercent}
      />
    </div>
  );
};

export default ProgressBar;
